import { Config } from 'final-form';

import { makeDeleteOperation } from '@occc-app/offerHelpers/operations/makeDeleteOperation';
import { OfferFragment } from '@occc/graphql';
import { constraintHelper } from '@occc-app/offerHelpers/utils/constraintHelper';
import {
  makeCreateDaysOfWeekConstraintOperation,
  makeCreateTimeOfDayConstrainOperation,
} from '@occc-app/offerHelpers/operations/constraints';
import { DirtyFieldsType, Operation } from '@occc-app/offerHelpers/types';
import { isFieldDirty } from '@occc-app/offerHelpers/utils';
import { DateTimeFormatParse, getUserTimezone, parseDate } from '@occc-app/lib/date';

import { validTimezones, FulfillmentWindowFormValues } from './types';

const getTimeOfDayConstraint = (offer?: OfferFragment) => {
  return offer?.constraints.find(constraintHelper.isTimeOfDay);
};

const getDaysOfWeekConstraint = (offer?: OfferFragment) => {
  return offer?.constraints.find(constraintHelper.isDaysOfWeek);
};

export const getDefaultTimezone = () => {
  const userTimezone = getUserTimezone();
  // If we are not in one of the 4 valid timezones, default to EST
  return validTimezones.includes(userTimezone) ? userTimezone : 'America/New_York';
};

export const transformOfferToFulfillmentWindowFormValue = (
  offer: OfferFragment,
): Config<FulfillmentWindowFormValues>['initialValues'] => {
  const timeOfDayConstraint = getTimeOfDayConstraint(offer);
  const daysOfWeekConstraint = getDaysOfWeekConstraint(offer);

  // TODO: validate if timezones different for different constraints?
  const timezone = daysOfWeekConstraint?.startOfDayTimezone || timeOfDayConstraint?.timezone;

  return {
    fulfillmentWindow: {
      isEnabled: !!(timeOfDayConstraint || daysOfWeekConstraint),
      timezone: timezone || getDefaultTimezone(),
      daysOfWeek: daysOfWeekConstraint
        ? {
            id: daysOfWeekConstraint.id,
            availableWeeklyOnDays: daysOfWeekConstraint.availableWeeklyOnDays,
          }
        : undefined,
      timeOfDay: timeOfDayConstraint
        ? {
            id: timeOfDayConstraint.id,
            availableDailyStartingAtTime: parseDate(
              timeOfDayConstraint.availableDailyStartingAtTime,
              DateTimeFormatParse.Time24hFull,
            ),
            availableDailyEndingAtTime: parseDate(
              timeOfDayConstraint.availableDailyEndingAtTime,
              DateTimeFormatParse.Time24hFull,
            ),
          }
        : undefined,
    },
  };
};

export const getFulfillmentWindowOperations = (
  offerId: string,
  values: FulfillmentWindowFormValues['fulfillmentWindow'],
  dirtyFields: DirtyFieldsType,
) => {
  const operations: Operation[] = [];

  const isTimezoneDirty = isFieldDirty<FulfillmentWindowFormValues>(
    dirtyFields,
    'fulfillmentWindow.timezone',
  );
  const { isEnabled, timeOfDay, daysOfWeek, timezone } = values;

  if (!isEnabled) {
    if (timeOfDay?.id) {
      operations.push(makeDeleteOperation('constraintDelete', timeOfDay.id));
    }
    if (daysOfWeek?.id) {
      operations.push(makeDeleteOperation('constraintDelete', daysOfWeek.id));
    }
    return operations;
  }

  if (
    isFieldDirty<FulfillmentWindowFormValues>(dirtyFields, 'fulfillmentWindow.timeOfDay') ||
    isTimezoneDirty
  ) {
    if (timeOfDay?.id) {
      operations.push(makeDeleteOperation('constraintDelete', timeOfDay.id));
    }
    if (
      timeOfDay?.availableDailyStartingAtTime &&
      timeOfDay?.availableDailyEndingAtTime &&
      timezone
    ) {
      operations.push(
        makeCreateTimeOfDayConstrainOperation(
          offerId,
          timeOfDay.availableDailyStartingAtTime,
          timeOfDay.availableDailyEndingAtTime,
          timezone,
        ),
      );
    }
  }

  if (
    isFieldDirty<FulfillmentWindowFormValues>(dirtyFields, 'fulfillmentWindow.daysOfWeek') ||
    isTimezoneDirty
  ) {
    if (daysOfWeek?.id) {
      operations.push(makeDeleteOperation('constraintDelete', daysOfWeek.id));
    }
    if (daysOfWeek?.availableWeeklyOnDays?.length && timezone) {
      operations.push(
        makeCreateDaysOfWeekConstraintOperation(
          offerId,
          daysOfWeek.availableWeeklyOnDays,
          timezone,
        ),
      );
    }
  }

  return operations;
};
